import { useCallback, useRef, useState } from "react";
import { env } from "@/lib/env";
import i18n from "@/lib/i18n";
import { SseError, sseFromPost, type SseFrame } from "@/lib/sse";
import { getSupabase, isSupabaseConfigured } from "@/lib/supabase";
import type { CookingStep, Recipe } from "@/types/menu";

export interface ChatMsg {
  role: "user" | "assistant";
  content: string;
}

export interface CookingChatState {
  messages: ChatMsg[];
  isStreaming: boolean;
  error: string | null;
}

/** Mirrors the backend limits (cooking_assistant.py) so a long
 *  conversation never turns into a 422 (audit M18). */
export const MAX_HISTORY_MESSAGES = 20;
export const MAX_MESSAGE_CHARS = 5000;

type ChatEvent =
  | { kind: "token"; text: string }
  | { kind: "done" }
  | { kind: "error"; message: string };

/** Map one SSE frame of `/cooking-assistant/chat` to a chat event. */
function toChatEvent(frame: SseFrame): ChatEvent {
  if (frame.event === "error") {
    return { kind: "error", message: decodeError(frame.data) };
  }
  if (frame.data === "[DONE]") return { kind: "done" };
  return { kind: "token", text: decodeToken(frame.data) };
}

/** Tokens are JSON-encoded by the backend (audit H12). */
function decodeToken(data: string): string {
  try {
    const parsed: unknown = JSON.parse(data);
    return typeof parsed === "string" ? parsed : data;
  } catch {
    return data;
  }
}

function decodeError(data: string): string {
  try {
    const parsed = JSON.parse(data) as { message?: unknown };
    if (typeof parsed.message === "string" && parsed.message) {
      return parsed.message;
    }
  } catch {
    // fall through
  }
  return i18n.t("cookingChat.interrupted");
}

/** Turn a non-2xx response (FastAPI `{"detail": ...}`) into a message. */
function httpErrorMessage(err: SseError): string {
  const body = err.body;
  if (typeof body === "object" && body !== null && "detail" in body) {
    const detail = (body as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
    if (
      typeof detail === "object" &&
      detail !== null &&
      (detail as { code?: unknown }).code === "premium_required"
    ) {
      return "premium_required";
    }
    return JSON.stringify(detail);
  }
  return i18n.t("cookingChat.http_error", { status: err.status });
}

class StreamError extends Error {}

export function useCookingChat(): CookingChatState & {
  send: (opts: {
    recipe: Recipe;
    currentStep?: CookingStep | null;
    message: string;
  }) => Promise<void>;
  reset: () => void;
} {
  const [state, setState] = useState<CookingChatState>({
    messages: [],
    isStreaming: false,
    error: null,
  });
  const abortRef = useRef<AbortController | null>(null);
  const historyRef = useRef<ChatMsg[]>([]);

  const send = useCallback(
    async (opts: {
      recipe: Recipe;
      currentStep?: CookingStep | null;
      message: string;
    }) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const userMsg: ChatMsg = { role: "user", content: opts.message };
      const priorHistory = historyRef.current;
      historyRef.current = [...priorHistory, userMsg];

      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, userMsg],
        isStreaming: true,
        error: null,
      }));

      let assistantContent = "";
      let bubbleShown = false;

      const showAssistant = (content: string) => {
        setState((prev) => {
          const msgs = [...prev.messages];
          if (bubbleShown && msgs[msgs.length - 1]?.role === "assistant") {
            msgs[msgs.length - 1] = { role: "assistant", content };
          } else {
            msgs.push({ role: "assistant", content });
          }
          return { ...prev, messages: msgs };
        });
        bubbleShown = true;
      };

      try {
        const headers: Record<string, string> = {};
        if (isSupabaseConfigured()) {
          const {
            data: { session },
          } = await getSupabase().auth.getSession();
          if (session?.access_token) {
            headers.Authorization = `Bearer ${session.access_token}`;
          }
        }

        // The household is resolved server-side (audit M19); history is
        // bounded to what the backend accepts (audit M18).
        const body = {
          recipe: opts.recipe,
          current_step: opts.currentStep ?? null,
          history: priorHistory.slice(-MAX_HISTORY_MESSAGES).map((m) => ({
            role: m.role,
            content: m.content.slice(0, MAX_MESSAGE_CHARS),
          })),
          message: opts.message.slice(0, MAX_MESSAGE_CHARS),
        };

        let done = false;
        for await (const event of sseFromPost<ChatEvent>({
          url: `${env.API_BASE_URL}/api/v1/cooking-assistant/chat`,
          body,
          headers,
          signal: controller.signal,
          parse: toChatEvent,
        })) {
          if (event.kind === "error") throw new StreamError(event.message);
          if (event.kind === "done") {
            done = true;
            break;
          }
          assistantContent += event.text;
          showAssistant(assistantContent);
        }

        if (!done) {
          // Stream closed without [DONE]: the answer is truncated.
          throw new StreamError(i18n.t("cookingChat.interrupted"));
        }
        if (!assistantContent) {
          throw new StreamError(i18n.t("cookingChat.empty"));
        }

        historyRef.current = [
          ...historyRef.current,
          { role: "assistant", content: assistantContent },
        ];
        setState((prev) => ({ ...prev, isStreaming: false }));
      } catch (e) {
        if (controller.signal.aborted) return;
        if (assistantContent) {
          // Keep what was received so far in the conversation.
          historyRef.current = [
            ...historyRef.current,
            { role: "assistant", content: assistantContent },
          ];
        }
        const message =
          e instanceof SseError
            ? httpErrorMessage(e)
            : e instanceof Error && e.message
              ? e.message
              : i18n.t("cookingChat.error_generic");
        setState((prev) => ({ ...prev, error: message, isStreaming: false }));
      }
    },
    [],
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    historyRef.current = [];
    setState({ messages: [], isStreaming: false, error: null });
  }, []);

  return { ...state, send, reset };
}
